
import Vue, { PropType } from "vue";

import TableCustom from "@/components/TableCustom.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";
import DateTimeRange from "@/components/DateTimeRange.vue";
import { RvsCalculationJob, RvsRawDataFile } from "@/clients/RvsCalculationClient";

const STATUS_OPTIONS = [
  { label: "All Status", value: null },
  { label: "Pending", value: "pending" },
  { label: "Processing", value: "processing" },
  { label: "Success", value: "success" },
  { label: "Failed", value: "failed" },
];

export default Vue.extend({
  name: "VibrationParameterTab",
  components: { TableCustom, SearchIcon, DateTimeRange },
  props: {
    // Owned by the page (single-job rule spans both tabs). Used for live per-file
    // status updates during a running spectrum job and to disable Re-calculate.
    activeJob: { type: Object as PropType<RvsCalculationJob | null>, default: null },
    jobBusy: { type: Boolean, default: false },
  },
  data() {
    const headers = [
      { text: "", value: "select", sortable: false, width: "48px" },
      { text: "Sensor ID", value: "sensorId" },
      { text: "File name", value: "fileName" },
      { text: "Raw data date", value: "rawDataDate" },
      { text: "Latest re-cal date", value: "latestCalDate" },
      { text: "Status", value: "processStatus", sortable: false },
    ];
    return {
      headers,
      files: [] as RvsRawDataFile[],
      selected: [] as RvsRawDataFile[],
      selectedSensorId: null as string | null,
      sensorSearch: null as string | null,
      sensorOptions: [] as string[],
      isLoadingSensors: false,
      statusFilter: null as string | null,
      statusOptions: STATUS_OPTIONS,
      dateFrom: null as string | null,
      dateTo: null as string | null,
      dateFilterKey: 0,
      order: "desc" as "asc" | "desc",
      isLoading: false,
      isCreatingJob: false,
      confirmOpen: false,
      debounce: 0 as any,
      currentPage: 1,
      pageSize: 25,
      totalPages: 0,
      totalItems: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "25", value: 25 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
    };
  },
  computed: {
    pageSelectableFiles(): RvsRawDataFile[] {
      return this.files.filter((file) => !this.isBusy(file));
    },
    allPageSelected(): boolean {
      const selectable = this.pageSelectableFiles;
      return selectable.length > 0 && selectable.every((file) => this.isSelected(file.id));
    },
    selectedSensorCount(): number {
      return new Set(this.selected.map((file) => file.sensorId)).size;
    },
  },
  created() {
    this.fetchSensorOptions();
  },
  beforeDestroy() {
    clearTimeout(this.debounce);
  },
  methods: {
    currentParams(extra: Record<string, any> = {}) {
      return {
        sensorId: this.selectedSensorId || undefined,
        rawDataDateFrom: this.dateFrom || undefined,
        rawDataDateTo: this.dateTo || undefined,
        processStatus: this.statusFilter || undefined,
        order: this.order,
        ...extra,
      };
    },
    async fetchSensorOptions(search?: string) {
      try {
        this.isLoadingSensors = true;
        this.sensorOptions = await (this as any).$dep.rvsCalculationService.getSensorIds(search || undefined);
      } catch {
        this.sensorOptions = [];
      } finally {
        this.isLoadingSensors = false;
      }
    },
    async fetchData(silent = false) {
      // The table is sensor-scoped: nothing loads until a sensor is selected.
      if (!this.selectedSensorId) {
        this.files = [];
        this.totalItems = 0;
        this.totalPages = 0;
        return;
      }
      try {
        if (!silent) this.isLoading = true;
        const response = await (this as any).$dep.rvsCalculationService.getRawDataFiles(
          this.currentParams({ page: this.currentPage, size: this.pageSize })
        );
        this.files = response.items || [];
        this.totalItems = response.total || 0;
        this.totalPages = this.totalItems > 0 ? Math.ceil(this.totalItems / this.pageSize) : 0;
      } catch {
        this.files = [];
        this.totalItems = 0;
        this.totalPages = 0;
      } finally {
        if (!silent) this.isLoading = false;
      }
    },
    isBusy(file: RvsRawDataFile): boolean {
      return file.processStatus === "pending" || file.processStatus === "processing";
    },
    isSelected(id: number): boolean {
      return this.selected.some((file) => file.id === id);
    },
    toggleRow(file: RvsRawDataFile) {
      if (this.isSelected(file.id)) {
        this.selected = this.selected.filter((item) => item.id !== file.id);
      } else {
        this.selected = [...this.selected, file];
      }
    },
    toggleSelectAll(checked: boolean) {
      const pageIds = this.pageSelectableFiles.map((file) => file.id);
      const others = this.selected.filter((file) => !pageIds.includes(file.id));
      this.selected = checked ? [...others, ...this.pageSelectableFiles] : others;
    },
    toggleSort() {
      this.order = this.order === "desc" ? "asc" : "desc";
      this.currentPage = 1;
      this.fetchData();
    },
    onFilterChange() {
      this.currentPage = 1;
      this.selected = []; // cross-page selection no longer valid after a filter change
      this.fetchData();
    },
    onDateRangeSubmit(range: { startDate: string; endDate: string }) {
      // DateTimeRange emits "YYYY-MM-DDT00:00:00.000Z" with a literal Z;
      // strip it so the backend compares naive datetimes (DB stores UTC+7).
      this.dateFrom = range.startDate.replace(".000Z", "");
      this.dateTo = range.endDate.replace(".000Z", "");
      this.onFilterChange();
    },
    clearAllFilters() {
      clearTimeout(this.debounce); // cancel pending sensor-search fetch
      this.statusFilter = null;
      this.dateFrom = null;
      this.dateTo = null;
      this.dateFilterKey += 1; // remount DateTimeRange -> placeholder
      this.onFilterChange();
    },
    onPageChange() {
      this.selected = []; // selection is per-page; drop it when navigating pages
      this.fetchData(true);
    },
    onPageSizeChange() {
      this.currentPage = 1;
      this.selected = [];
      this.fetchData();
    },
    chipOf(status: string) {
      if (status === "pending") return { label: "⏳ Pending", bgClass: "bg-[#FCA239]" };
      if (status === "processing") return { label: "Processing...", bgClass: "bg-[#307DF1]" };
      if (status === "failed") return { label: "✕ Failed", bgClass: "bg-[#D9263B] cursor-help" };
      return { label: "✓ Success", bgClass: "bg-[#2EC577]" };
    },
    formatTimestamp(timestamp: string | null | undefined) {
      if (!timestamp) return "-";
      return (this as any).$date(timestamp).format("DD/MM/YYYY HH:mm");
    },
    async startJob() {
      if (!this.selected.length) return;
      try {
        this.isCreatingJob = true;
        const { jobId } = await (this as any).$dep.rvsCalculationService.createJob(this.selected.map((file) => file.id));
        this.confirmOpen = false;
        this.selected = [];
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: "Calculation started — you can leave this page",
          type: "success",
        });
        // The page owns polling/banner; hand it the new job id.
        this.$emit("job-created", jobId);
        this.fetchData(true);
      } catch (error: any) {
        this.$store.dispatch("openAlertMessage", {
          open: true,
          message: error?.response?.data?.detail || "error: failed to start calculation job",
          type: "error",
        });
      } finally {
        this.isCreatingJob = false;
      }
    },
    applyJobFilesToTable(job: RvsCalculationJob) {
      const byId: Record<number, RvsRawDataFile> = {};
      (job.files || []).forEach((file) => {
        byId[file.id] = file;
      });
      this.files = this.files.map((file) => (byId[file.id] ? { ...file, ...byId[file.id] } : file));
    },
  },
  watch: {
    sensorSearch(search: string | null) {
      // v-autocomplete echoes the selected value back into search-input; skip that.
      if (search !== null && search === this.selectedSensorId) return;
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => this.fetchSensorOptions(search || undefined), 600);
    },
    activeJob(job: RvsCalculationJob | null, previous: RvsCalculationJob | null) {
      // Only spectrum jobs touch this table.
      if (job && job.type === "spectrum") this.applyJobFilesToTable(job);
      // On a running -> terminal transition, reload authoritative rows from server.
      if (previous?.status === "running" && job && job.status !== "running" && job.type === "spectrum") {
        this.fetchData(true);
      }
    },
  },
});
